import { Emitter } from '../core/emitter';
import { Rng } from '../core/rng';
import { Ai, scaledProfile } from './ai';
import { BOUT, DOTTIE_FIGHT_LINES, LINGO_CALLOUTS, MOVES, STAGE } from './data';
import type {
  BoutOutcome,
  BoutPhase,
  FighterState,
  FightEvent,
  MoveDef,
  MoveId,
  OpponentDef,
  PlayerInput,
  Projectile,
  ProjectileSpec,
  Side,
} from './types';

export interface BoutOptions {
  opponent: OpponentDef;
  /** Night number in this session; later nights are rowdier. */
  level: number;
  seed: number;
  round: number;
  totalRounds: number;
  /** Super meter carried over from the previous bout. */
  superMeter: number;
}

export interface BoutStats {
  hits: number;
  parries: number;
  supers: number;
  chairCatches: number;
  maxCombo: number;
}

type Buffered = { action: 'jab' | 'toss' | 'super'; t: number };

function fighter(side: Side, x: number, meter: number): FighterState {
  return {
    side,
    x,
    facing: side === 'player' ? 1 : -1,
    vx: 0,
    meter,
    maxMeter: meter,
    action: null,
    blocking: false,
    blockHeldFor: 0,
    stun: 0,
    parried: 0,
    walking: 0,
    walkT: 0,
    flash: 0,
    cooldowns: {},
    damageTaken: 0,
  };
}

/**
 * One fight against one rowdy customer. Their RAGE bar drains as you land
 * hits; drain it and they calm down. Your COOL bar drains when they land hits.
 */
export class Bout {
  readonly events = new Emitter<FightEvent>();
  readonly opponentDef: OpponentDef;
  readonly options: BoutOptions;
  readonly player: FighterState;
  readonly opponent: FighterState;
  projectiles: Projectile[] = [];
  phase: BoutPhase = 'intro';
  phaseT = 0;
  t = 0;
  timeLeft: number = BOUT.time;
  hitstop = 0;
  superMeter: number;
  combo = 0;
  comboTimer = 0;
  outcome: BoutOutcome | null = null;
  readonly stats: BoutStats = { hits: 0, parries: 0, supers: 0, chairCatches: 0, maxCombo: 0 };
  /** Set while the ALL THE WAY super is landing its hits. */
  superSeq: { hits: number; timer: number } | null = null;

  private readonly rng: Rng;
  private readonly ai: Ai;
  private buffer: Buffered | null = null;
  private projectileId = 0;
  private introStage = 0;

  constructor(options: BoutOptions) {
    this.options = options;
    this.opponentDef = options.opponent;
    this.rng = new Rng(options.seed);
    this.player = fighter('player', 84, BOUT.playerCool);
    this.opponent = fighter('opponent', 172, options.opponent.rage);
    this.superMeter = Math.min(BOUT.superCost, options.superMeter);
    this.ai = new Ai(
      options.opponent,
      scaledProfile(options.opponent.ai, options.level),
      this.rng.fork('ai'),
    );
  }

  get flawless(): boolean {
    return this.player.damageTaken === 0;
  }

  get superReady(): boolean {
    return this.superMeter >= BOUT.superCost;
  }

  /** Advance by one fixed step. `input` edges (jab/toss/super) are consumed here. */
  update(dt: number, input: PlayerInput): void {
    if (this.phase === 'done') return;
    if (input.super) this.buffer = { action: 'super', t: this.t };
    else if (input.toss) this.buffer = { action: 'toss', t: this.t };
    else if (input.jab) this.buffer = { action: 'jab', t: this.t };
    if (this.hitstop > 0) {
      this.hitstop -= dt;
      return;
    }
    this.t += dt;
    this.phaseT += dt;

    switch (this.phase) {
      case 'intro':
        this.updateIntro();
        break;
      case 'fight':
        this.updateFight(dt, input);
        break;
      case 'ko':
        this.player.flash = Math.max(0, this.player.flash - dt);
        this.opponent.flash = Math.max(0, this.opponent.flash - dt);
        this.physics(dt);
        if (this.phaseT >= BOUT.koSeconds) this.setPhase('done');
        break;
    }
  }

  private emit(e: FightEvent): void {
    this.events.emit(e);
  }

  private setPhase(phase: BoutPhase): void {
    this.phase = phase;
    this.phaseT = 0;
    this.emit({ type: 'phase', phase });
  }

  private updateIntro(): void {
    const { round, totalRounds } = this.options;
    if (this.introStage === 0) {
      this.introStage = 1;
      const label = round === totalRounds ? 'FINAL ROUND' : `ROUND ${round}`;
      this.emit({
        type: 'banner',
        text: label,
        sub: this.opponentDef.name,
        style: 'round',
        duration: 1.2,
      });
      this.emit({ type: 'say', who: 'opponent', text: this.opponentDef.lines.intro, duration: 2 });
      this.emit({ type: 'sfx', name: 'bell' });
    } else if (this.introStage === 1 && this.phaseT >= 1.25) {
      this.introStage = 2;
      this.emit({
        type: 'banner',
        text: 'FIGHT!',
        sub: '(to calm them down)',
        style: 'fight',
        duration: 0.9,
      });
      if (round === 1) {
        this.emit({
          type: 'say',
          who: 'dottie',
          text: this.rng.pick(DOTTIE_FIGHT_LINES.intro),
          duration: 2.4,
        });
      }
    }
    if (this.phaseT >= BOUT.introSeconds) this.setPhase('fight');
  }

  private updateFight(dt: number, input: PlayerInput): void {
    this.timeLeft = Math.max(0, this.timeLeft - dt);
    this.controlPlayer(dt, input);
    this.controlOpponent(dt);
    if (this.superSeq) this.updateSuper(dt);
    else {
      this.advanceAction(this.player, this.opponent, dt);
      this.advanceAction(this.opponent, this.player, dt);
    }
    this.physics(dt);
    this.updateProjectiles(dt);
    for (const f of [this.player, this.opponent]) {
      f.stun = Math.max(0, f.stun - dt);
      f.parried = Math.max(0, f.parried - dt);
      f.flash = Math.max(0, f.flash - dt);
      for (const k of Object.keys(f.cooldowns) as MoveId[])
        f.cooldowns[k] = Math.max(0, (f.cooldowns[k] ?? 0) - dt);
    }
    if (this.comboTimer > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) this.combo = 0;
    }

    if (this.opponent.meter <= 0) this.end('calmed');
    else if (this.player.meter <= 0) this.end('lost');
    else if (this.timeLeft <= 0) {
      const calmer =
        this.opponent.meter / this.opponent.maxMeter <= this.player.meter / this.player.maxMeter;
      this.end(calmer ? 'calmed' : 'lost', true);
    }
  }

  /* ───────────────────────── control ───────────────────────── */

  private controlPlayer(dt: number, input: PlayerInput): void {
    const p = this.player;
    if (p.stun > 0 || p.parried > 0 || this.superSeq) {
      // You can hold block through blockstun, but you can't start anything.
      if (p.stun > 0 && p.blocking && input.block) p.blockHeldFor += dt;
      return;
    }
    const buf = this.buffer && this.t - this.buffer.t <= BOUT.inputBuffer ? this.buffer : null;
    if (buf && p.action) {
      const a = p.action;
      if (buf.action === 'jab' && a.def.chain && a.t >= a.def.startup) {
        a.queued = true;
        this.buffer = null;
      }
    } else if (buf) {
      let started = false;
      if (buf.action === 'super') {
        if (this.superReady) {
          this.superMeter -= BOUT.superCost;
          this.stats.supers++;
          this.startAction(p, 'super');
          this.hitstop = BOUT.hitstop.super;
          this.emit({ type: 'super', side: 'player' });
          this.emit({ type: 'banner', text: 'ALL THE WAY!', style: 'super', duration: 1.1 });
          this.emit({ type: 'sfx', name: 'super' });
          started = true;
        }
      } else if (buf.action === 'toss') {
        const busy = this.projectiles.some((pr) => pr.owner === 'player');
        if ((p.cooldowns.toss ?? 0) <= 0 && !busy) {
          this.startAction(p, 'toss');
          started = true;
        }
      } else {
        this.startAction(p, 'jab');
        started = true;
      }
      if (started) this.buffer = null;
    }
    if (p.action) return;

    if (input.block) {
      if (!p.blocking) {
        p.blocking = true;
        p.blockHeldFor = 0;
      } else {
        p.blockHeldFor += dt;
      }
      p.walking = 0;
      return;
    }
    p.blocking = false;
    const dir = ((input.right ? 1 : 0) - (input.left ? 1 : 0)) as -1 | 0 | 1;
    p.walking = dir;
    if (dir !== 0) {
      const speed = dir === p.facing ? BOUT.playerWalk.forward : BOUT.playerWalk.back;
      p.x += dir * speed * dt;
      p.walkT += dt;
    }
  }

  private controlOpponent(dt: number): void {
    const o = this.opponent;
    const intent = this.ai.update(dt, this.t, o, this.player, this.projectiles);
    if (o.stun > 0 || o.parried > 0 || o.action || this.superSeq) {
      if (o.stun <= 0) o.blocking = false;
      return;
    }
    if (intent.move) {
      o.blocking = false;
      this.startAction(o, intent.move);
      return;
    }
    if (intent.block) {
      if (!o.blocking) o.blockHeldFor = 0;
      o.blocking = true;
      o.blockHeldFor += dt;
      o.walking = 0;
      return;
    }
    o.blocking = false;
    o.walking = intent.walk;
    if (intent.walk !== 0) {
      o.x += intent.walk * this.opponentDef.walkSpeed * dt;
      o.walkT += dt;
    }
  }

  private startAction(f: FighterState, id: MoveId): void {
    const def = MOVES[id];
    f.action = { def, t: 0, hit: false, fired: false, queued: false };
    f.blocking = false;
    f.walking = 0;
    if (def.cooldown) f.cooldowns[id] = def.cooldown;
    if (id === 'taunt' && f.side === 'opponent') {
      this.emit({
        type: 'say',
        who: 'opponent',
        text: this.rng.pick(this.opponentDef.lines.taunts),
        duration: 1.2,
      });
    }
  }

  /* ───────────────────────── moves ───────────────────────── */

  private advanceAction(f: FighterState, foe: FighterState, dt: number): void {
    const a = f.action;
    if (!a) return;
    const d = a.def;
    const before = a.t;
    a.t += dt;
    const activeStart = d.startup;
    const activeEnd = d.startup + d.active;
    const total = activeEnd + d.recovery;

    if ((d.id === 'toss' || d.id === 'throw') && !a.fired && a.t >= activeStart) {
      a.fired = true;
      const spec = d.projectile ?? this.opponentDef.projectile;
      if (spec) this.fire(f, spec);
    }
    if (d.dash && a.t >= activeStart && before < activeEnd && !a.hit) {
      f.x += f.facing * d.dash.speed * dt;
    }
    if (
      d.reach > 0 &&
      !a.hit &&
      a.t >= activeStart &&
      before < activeEnd &&
      this.inReach(f, foe, d.reach)
    ) {
      a.hit = true;
      if (d.id === 'super') {
        this.beginSuperCombo(foe);
        return;
      }
      this.resolveHit(f, foe, d.damage, d.knockback, d.hitstun, !!d.heavy, false);
    }
    if (d.reach > 0 && !a.hit && before < activeEnd && a.t >= activeEnd) {
      this.emit({ type: 'sfx', name: 'whiff' });
    }
    // Chains fire as soon as the active window closes — that's what makes combos flow.
    if (a.queued && d.chain && a.t >= activeEnd + 0.03) {
      this.startAction(f, d.chain);
      return;
    }
    if (a.t >= total) f.action = null;
  }

  private inReach(att: FighterState, def: FighterState, reach: number): boolean {
    const dx = (def.x - att.x) * att.facing;
    return dx > -4 && dx - STAGE.bodyHalf <= reach;
  }

  private beginSuperCombo(foe: FighterState): void {
    this.superSeq = { hits: 0, timer: 0 };
    foe.action = null;
    foe.blocking = false;
    foe.stun = 1;
    this.emit({ type: 'sfx', name: 'crowd' });
  }

  private updateSuper(dt: number): void {
    const seq = this.superSeq;
    if (!seq) return;
    const foe = this.opponent;
    foe.stun = Math.max(foe.stun, 0.3);
    seq.timer += dt;
    while (seq.timer >= BOUT.superHitEvery && seq.hits < BOUT.superHits) {
      seq.timer -= BOUT.superHitEvery;
      seq.hits++;
      const last = seq.hits === BOUT.superHits;
      this.applyDamage(foe, BOUT.superDamage);
      foe.flash = 0.08;
      this.stats.hits++;
      this.combo++;
      this.comboTimer = BOUT.comboWindow;
      this.stats.maxCombo = Math.max(this.stats.maxCombo, this.combo);
      this.emit({
        type: 'callout',
        text: last ? 'ALL THE WAY!' : (LINGO_CALLOUTS[seq.hits - 1] ?? 'ALL THE WAY!'),
        x: foe.x,
        side: 'opponent',
        big: last,
      });
      this.emit({
        type: 'hit',
        target: 'opponent',
        x: foe.x,
        damage: BOUT.superDamage,
        blocked: false,
        heavy: last,
      });
      this.emit({ type: 'sfx', name: last ? 'heavy' : 'punch' });
      if (last) {
        foe.vx =
          this.player.facing *
          (MOVES.super.knockback / this.opponentDef.weight) *
          BOUT.knockbackDecay;
        foe.stun = MOVES.super.hitstun;
        this.hitstop = BOUT.hitstop.heavy * 1.5;
        this.emit({ type: 'shake', amount: 0.6 });
        this.superSeq = null;
        const a = this.player.action;
        if (a) a.t = a.def.startup + a.def.active;
      }
    }
  }

  private fire(f: FighterState, spec: ProjectileSpec): void {
    const p: Projectile = {
      id: ++this.projectileId,
      owner: f.side,
      kind: spec.kind,
      x: f.x + f.facing * 14,
      vx: f.facing * spec.speed,
      damage: spec.damage,
      hitstun: spec.hitstun,
      knockback: spec.knockback,
      age: 0,
    };
    this.projectiles.push(p);
    this.emit({ type: 'projectile', projectile: p });
    this.emit({ type: 'sfx', name: 'toss' });
    if (f.side === 'player')
      this.emit({ type: 'callout', text: 'HASH BROWN!', x: f.x, side: 'player' });
  }

  private applyDamage(f: FighterState, amount: number): void {
    f.meter = Math.max(0, f.meter - amount);
    f.damageTaken += amount;
  }

  /** Returns what happened so projectiles know whether they were caught. */
  private resolveHit(
    att: FighterState | null,
    def: FighterState,
    damage: number,
    knockback: number,
    hitstun: number,
    heavy: boolean,
    projectile: boolean,
    fromDir?: 1 | -1,
    chair = false,
  ): 'hit' | 'blocked' | 'parried' {
    const dir: 1 | -1 = fromDir ?? att?.facing ?? 1;
    const facingThreat = def.facing === -dir;
    const weight = def.side === 'opponent' ? this.opponentDef.weight : 1;

    if (def.blocking && facingThreat) {
      if (def.side === 'player' && def.blockHeldFor <= BOUT.parryWindow) {
        this.stats.parries++;
        if (att && !projectile) {
          att.parried = 0.7;
          att.action = null;
        }
        this.superMeter = Math.min(
          BOUT.superCost,
          this.superMeter + (chair ? BOUT.superGain.chairCatch : BOUT.superGain.parry),
        );
        if (chair) this.stats.chairCatches++;
        this.hitstop = BOUT.hitstop.parry;
        this.emit({ type: 'parry', x: def.x, chair });
        this.emit({
          type: 'callout',
          text: chair ? 'CAUGHT IT!' : 'PARRY!',
          x: def.x,
          side: 'player',
          big: chair,
        });
        this.emit({ type: 'sfx', name: chair ? 'chairCatch' : 'parry' });
        if (this.rng.chance(chair ? 1 : 0.45)) {
          this.emit({
            type: 'say',
            who: 'dottie',
            text: this.rng.pick(DOTTIE_FIGHT_LINES.parry),
            duration: 1.8,
          });
        }
        return 'parried';
      }
      this.applyDamage(def, damage * BOUT.blockChip);
      def.stun = Math.max(def.stun, 0.16);
      def.vx = dir * ((knockback * 0.5) / weight) * BOUT.knockbackDecay;
      // Swinging into a guard pushes you back out of range.
      if (att && !projectile) att.vx = -att.facing * 7 * BOUT.knockbackDecay;
      this.hitstop = BOUT.hitstop.light;
      this.emit({
        type: 'hit',
        target: def.side,
        x: def.x,
        damage: damage * BOUT.blockChip,
        blocked: true,
        heavy: false,
      });
      this.emit({ type: 'sfx', name: 'block' });
      return 'blocked';
    }

    // Armoured windups soak light hits and keep coming.
    const armored =
      def.action?.def.armor === true && def.action.t < def.action.def.startup && !heavy;
    if (armored) {
      this.applyDamage(def, damage);
      def.flash = 0.08;
      this.hitstop = BOUT.hitstop.light;
      this.emit({ type: 'hit', target: def.side, x: def.x, damage, blocked: false, heavy: false });
      this.emit({ type: 'callout', text: 'TOUGH!', x: def.x, side: def.side });
      this.emit({ type: 'sfx', name: 'block' });
      return 'hit';
    }

    this.applyDamage(def, damage);
    def.stun = hitstun;
    def.action = null;
    def.blocking = false;
    def.flash = 0.12;
    def.vx = dir * (knockback / weight) * BOUT.knockbackDecay;
    this.hitstop = heavy ? BOUT.hitstop.heavy : BOUT.hitstop.light;
    if (heavy) this.emit({ type: 'shake', amount: 0.4 });

    if (def.side === 'opponent') {
      this.combo++;
      this.comboTimer = BOUT.comboWindow;
      this.stats.hits++;
      this.stats.maxCombo = Math.max(this.stats.maxCombo, this.combo);
      this.superMeter = Math.min(BOUT.superCost, this.superMeter + BOUT.superGain.hit);
      const word = LINGO_CALLOUTS[(this.combo - 1) % LINGO_CALLOUTS.length] ?? 'SCATTERED!';
      this.emit({
        type: 'callout',
        text: this.combo >= LINGO_CALLOUTS.length ? 'ALL THE WAY!' : word,
        x: def.x,
        side: 'opponent',
        big: this.combo >= 3,
      });
    } else {
      this.combo = 0;
      this.comboTimer = 0;
      this.superMeter = Math.min(BOUT.superCost, this.superMeter + BOUT.superGain.hurt);
    }
    this.emit({ type: 'hit', target: def.side, x: def.x, damage, blocked: false, heavy });
    this.emit({ type: 'sfx', name: heavy ? 'heavy' : 'punch' });
    return 'hit';
  }

  /* ───────────────────────── world ───────────────────────── */

  private physics(dt: number): void {
    const decay = Math.exp(-BOUT.knockbackDecay * dt);
    for (const f of [this.player, this.opponent]) {
      if (Math.abs(f.vx) > 0.5) {
        f.x += f.vx * dt;
        f.vx *= decay;
      } else {
        f.vx = 0;
      }
      f.x = Math.max(STAGE.left, Math.min(STAGE.right, f.x));
    }
    const p = this.player;
    const o = this.opponent;
    const gap = o.x - p.x;
    if (Math.abs(gap) < STAGE.minGap) {
      const push = (STAGE.minGap - Math.abs(gap)) / 2;
      const sign = gap >= 0 ? 1 : -1;
      p.x -= sign * push;
      o.x += sign * push;
      for (const f of [p, o]) f.x = Math.max(STAGE.left, Math.min(STAGE.right, f.x));
      // If one of them is pinned to the wall, shove the other out instead.
      if (Math.abs(o.x - p.x) < STAGE.minGap) {
        if (p.x <= STAGE.left || p.x >= STAGE.right) o.x = p.x + sign * STAGE.minGap;
        else p.x = o.x - sign * STAGE.minGap;
      }
    }
    if (this.phase === 'fight') {
      for (const [self, foe] of [
        [p, o],
        [o, p],
      ] as const) {
        if (!self.action && foe.x !== self.x) self.facing = foe.x > self.x ? 1 : -1;
      }
    }
  }

  private updateProjectiles(dt: number): void {
    const alive: Projectile[] = [];
    for (const pr of this.projectiles) {
      pr.x += pr.vx * dt;
      pr.age += dt;
      if (pr.x < STAGE.left - 30 || pr.x > STAGE.right + 30) continue;
      const target = pr.owner === 'player' ? this.opponent : this.player;
      if (Math.abs(pr.x - target.x) <= STAGE.bodyHalf + 4) {
        const dir: 1 | -1 = pr.vx > 0 ? 1 : -1;
        const attacker = pr.owner === 'player' ? this.player : this.opponent;
        this.resolveHit(
          attacker,
          target,
          pr.damage,
          pr.knockback,
          pr.hitstun,
          pr.kind === 'chair',
          true,
          dir,
          pr.kind === 'chair',
        );
        continue;
      }
      alive.push(pr);
    }
    // Projectiles that meet in the middle cancel out.
    for (const a of alive) {
      for (const b of alive) {
        if (a === b || a.owner === b.owner || a.age < 0 || b.age < 0) continue;
        if (Math.abs(a.x - b.x) < 10) {
          a.age = -1;
          b.age = -1;
          const x = (a.x + b.x) / 2;
          this.emit({ type: 'clash', x });
          this.emit({ type: 'callout', text: 'CLASH!', x, side: 'player' });
          this.emit({ type: 'sfx', name: 'clash' });
        }
      }
    }
    this.projectiles = alive.filter((p) => p.age >= 0);
  }

  private end(outcome: BoutOutcome, timeUp = false): void {
    if (this.outcome) return;
    this.outcome = outcome;
    this.projectiles = [];
    this.superSeq = null;
    for (const f of [this.player, this.opponent]) {
      f.action = null;
      f.blocking = false;
      f.walking = 0;
    }
    this.setPhase('ko');
    this.hitstop = 0.3;
    const flawless = this.flawless;
    if (outcome === 'calmed') {
      const sub = timeUp
        ? 'TIME! (calm enough)'
        : flawless
          ? 'FLAWLESS SERVICE'
          : this.opponentDef.name;
      this.emit({ type: 'banner', text: 'CALMED DOWN!', sub, style: 'good', duration: 2.2 });
      this.emit({ type: 'say', who: 'opponent', text: this.opponentDef.lines.calm, duration: 2.6 });
      this.emit({ type: 'sfx', name: 'calm' });
      this.emit({
        type: 'say',
        who: 'dottie',
        text: this.rng.pick(DOTTIE_FIGHT_LINES.calm),
        duration: 2.2,
      });
    } else {
      const sub = timeUp ? 'TIME! THEY’RE STILL MAD' : `${this.opponentDef.name} WINS`;
      this.emit({ type: 'banner', text: 'YOU LOST YOUR COOL', sub, style: 'lose', duration: 2.2 });
      this.emit({ type: 'say', who: 'opponent', text: this.opponentDef.lines.win, duration: 2.2 });
      this.emit({ type: 'sfx', name: 'lose' });
      this.emit({
        type: 'say',
        who: 'dottie',
        text: this.rng.pick(DOTTIE_FIGHT_LINES.lose),
        duration: 2.2,
      });
    }
    this.emit({ type: 'end', outcome, flawless });
  }

  /** For tests & bots: which move is the player currently doing? */
  get playerMove(): MoveDef | null {
    return this.player.action?.def ?? null;
  }
}
